import type { UseQueryResult } from '@tanstack/react-query';
import { useQueries } from '@tanstack/react-query';

import { useAppState } from '../../AppStateProvider';
import { mergeContact } from '../../domain/contact';
import type { EmailLengthText, GenerateEmailQuery, GeneratedEmail } from '../../domain/generatedEmail';
import type { Insight } from '../../domain/insight';
import { InsightType } from '../../domain/insightType';
import { InsightSignalType } from '../../domain/signal';
import type { SiteTrend } from '../../domain/siteTrend';
import { useDispatchTrackEventWithLocation } from '../../services/tracking/hooks/useDispatchTrackEventWithLocation';
import { useCompanyQuery } from '../company/useCompanyQuery';
import { useLinkedinContactQuery } from '../contacts/linkedinContact/useLinkedinContactQuery';
import { useUserSettings } from '../user/useUserSettings';

import { insightsApi } from './api';
import type { GenerateEmailPayload } from './api';
import { decreaseEmailsQuota } from './decreaseEmailsQuota';
import { insightsQueryKey } from './insightsQueryKey';

const useMergedContact = () => {
  const contactQuery = useLinkedinContactQuery();
  const { collectedContactInfo } = useAppState();
  return mergeContact(contactQuery?.data, collectedContactInfo);
};

const initialGenerateEmailPayload: GenerateEmailPayload = {
  receiver: { companyName: '', contactName: '', contactRole: '', companyWebsite: '' },
  siteTrendsInsightModels: [],
  opportunitiesInsightModels: [],
  technologySignalInsightModels: [],
  trafficSignalInsightModels: [],
  newsSignalInsightModels: [],
  adNetworkSignalInsightModels: [],
  newMarketsSignalInsightModels: [],
  textLength: 'medium',
  languageCode: 'en',
};

export type EmailGenerationParams = { insight: Insight; count: number };

export type EmailGenerationResult = { insight: Insight; queries: GenerateEmailQuery[] };

export type GenerateEmailResponse = { email: GeneratedEmail; requestId: number; textLength: EmailLengthText };

export const useGenerateEmails = (params: EmailGenerationParams[], requestedTextLength: EmailLengthText) => {
  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();

  const contact = useMergedContact();
  const company = useCompanyQuery().data;

  const { emailGenerationOptions } = useAppState();

  const { getUserSAMLanguageCode } = useUserSettings();
  const languageCode = getUserSAMLanguageCode();

  const receiver = {
    companyName: company?.name || '',
    companyWebsite: company?.domain || '',
    contactName: contact?.name || 'John Doe',
    contactRole: contact?.role || 'CEO',
  };

  const generateEmailByInsight = async (srcInsight: Insight): Promise<GenerateEmailResponse> => {
    let insight = srcInsight;
    const payload: GenerateEmailPayload = {
      ...initialGenerateEmailPayload,
      receiver: { ...receiver },
      textLength: requestedTextLength,
      languageCode,
    };

    if (insight.insightType === InsightType.Signal && insight.type === InsightSignalType.Traffic) {
      const [siteTrend] = insight.siteTrends;
      if (siteTrend) {
        payload.siteTrendsInsightModels = [siteTrend as SiteTrend];
      }
      insight = { ...insight, siteTrends: [] };
    }

    const key = dtoKeyByInsight(insight);
    if (key) {
      payload[key] = [insight];
    }

    const result = await insightsApi.generateEmail(payload, emailGenerationOptions.sessionId);
    if (result) {
      const newQuota = decreaseEmailsQuota();
      if (newQuota && newQuota.remaining <= 0) {
        dispatchTrackEventWithLocation('usage limits', 'limit reached', 'insights');
      }
    }
    return result;
  };

  const makeQueries = (params: EmailGenerationParams[]) => {
    return params.flatMap(({ insight, count }) => {
      return Array.from({ length: count }, (_: unknown, index: number) => ({
        queryKey: insightsQueryKey.generateEmail({ id: insight.id, index, languageCode }),
        queryFn: () => generateEmailByInsight(insight),
      }));
    });
  };

  const groupByInsights = (
    params: EmailGenerationParams[],
    queryResults: UseQueryResult<GenerateEmailResponse, unknown>[]
  ) => {
    const queryResultsCopy = [...queryResults];
    const emailGenerationResults = params.reduce((acc, { insight, count }) => {
      const queries = queryResultsCopy.splice(0, count).map(({ data, isLoading, isSuccess }) => ({
        email: data?.email,
        isLoading,
        isSuccess,
        requestId: data?.requestId,
        textLength: data?.textLength,
      }));
      acc.push({ insight, queries });
      return acc;
    }, [] as EmailGenerationResult[]);

    return emailGenerationResults;
  };

  const queryResults = useQueries({ queries: makeQueries(params) });
  return groupByInsights(params, queryResults);
};

const dtoKeyByInsight = (insight: Insight) => {
  switch (insight.insightType) {
    case InsightType.Opportunity:
      return 'opportunitiesInsightModels';
    case InsightType.SiteTrends:
      return 'siteTrendsInsightModels';
    case InsightType.Signal:
      switch (insight.type) {
        case InsightSignalType.Technology:
          return 'technologySignalInsightModels';
        case InsightSignalType.Traffic:
          if ('trafficChanges' in insight.filters) {
            return 'trafficSignalInsightModels';
          }
          return 'newMarketsSignalInsightModels';
        case InsightSignalType.News:
          return 'newsSignalInsightModels';
        case InsightSignalType.AdNetwork:
          return 'adNetworkSignalInsightModels';
        default:
          return null;
      }
    default:
      return null;
  }
};
